import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { sendBroadcastNotification } from "../../services/apiService";

const professionOptions = [
  { label: "Student", value: "student" },
  { label: "Professional", value: "professional" },
  { label: "Self Employed", value: "selfEmployed" },
  { label: "Retired", value: "retired" },
];

export default function NotificationComposerModal({
  show,
  onClose,
  defaultUserId,
  defaultUserName,
  allowTargetSelection = false,
  onSuccess,
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sendToAll, setSendToAll] = useState(!defaultUserId);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setTitle("");
      setBody("");
      setSelectedCategories([]);
      setError("");
      setSendToAll(!defaultUserId);
    }
  }, [show, defaultUserId]);

  const toggleCategory = (value) => {
    setSelectedCategories((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim() || !body.trim()) {
      setError("Title and body are required.");
      return;
    }

    if (!allowTargetSelection && !defaultUserId) {
      setError("Missing user id for targeted notification.");
      return;
    }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      image: "",
      send_to_all: allowTargetSelection ? sendToAll : false,
      profession_categories: null,
      user_id: allowTargetSelection ? null : defaultUserId,
    };

    if (allowTargetSelection) {
      if (payload.send_to_all) {
        payload.profession_categories = null;
      } else if (selectedCategories.length > 0) {
        payload.profession_categories = selectedCategories;
      } else {
        setError("Select at least one profession category before sending.");
        return;
      }
    }

    if (!payload.send_to_all && !payload.profession_categories && !payload.user_id) {
      setError("Select a target audience before sending.");
      return;
    }

    try {
      setSending(true);
      const response = await sendBroadcastNotification(payload);
      const message = response?.data?.message || "Notification sent successfully.";
      onSuccess?.(message, "success");
      onClose?.();
    } catch (err) {
      const serverError =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to send notification.";
      setError(serverError);
      onSuccess?.(serverError, "danger");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show fade d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content"
          style={{
            background: "#ffffff",
            color: "#0f172a",
            border: "1px solid #e5e7eb",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
          }}
        >
          <div
            className="modal-header"
            style={{
              borderBottom: "1px solid #e5e7eb",
              background: "#ffffff",
            }}
          >
            <h5 className="modal-title">
              {allowTargetSelection ? "Broadcast Notification" : "Send Notification"}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}

              {allowTargetSelection && (
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sendToAllSwitch"
                      checked={sendToAll}
                      onChange={(e) => setSendToAll(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="sendToAllSwitch">
                      Send to all users
                    </label>
                  </div>
                </div>
              )}

              {allowTargetSelection && !sendToAll && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Profession categories</label>
                  <div className="d-flex flex-wrap gap-3">
                    {professionOptions.map((option) => (
                      <div className="form-check" key={option.value}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`cat-${option.value}`}
                          checked={selectedCategories.includes(option.value)}
                          onChange={() => toggleCategory(option.value)}
                        />
                        <label className="form-check-label" htmlFor={`cat-${option.value}`}>
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!allowTargetSelection && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Recipient</label>
                  <input
                    type="text"
                    className="form-control"
                    value={defaultUserName || defaultUserId || ""}
                    readOnly
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-semibold">Title</label>
                <input
                  type="text"
                  className="form-control"
                  maxLength={30}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter notification title"
                  required
                />
                <small className="text-muted">{30 - title.length} characters left</small>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Body</label>
                <textarea
                  className="form-control"
                  rows="3"
                  maxLength={150}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter notification message"
                  required
                />
                <small className="text-muted">{150 - body.length} characters left</small>
              </div>

            </div>

            <div
              className="modal-footer"
              style={{
                borderTop: "1px solid #e5e7eb",
                background: "#ffffff",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={sending}
                style={{
                  background: "#f8fafc",
                  color: "#0f172a",
                  border: "1px solid #e5e7eb",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending}
                style={{
                  background: "linear-gradient(135deg, #2563eb, #22c55e)",
                  border: "none",
                  fontWeight: 700,
                  color: "#0b1220",
                  minWidth: "140px",
                }}
              >
                {sending ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  "Send Notification"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
